import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import ComponentAssignmentViewer from '../components/ComponentAssignmentViewer';
import CriticalErrorPopup from '../components/CriticalErrorPopup';
import ProjectComments from '../components/ProjectComments';

const ComponentSubmissions = ({ userData }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [componentSubmission, setComponentSubmission] = useState(null);
  const [component, setComponent] = useState(null);
  const [assignment, setAssignment] = useState(null);
  const [assignmentSubmission, setAssignmentSubmission] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [allowResubmission, setAllowResubmission] = useState(false);
  const [updatingResubmission, setUpdatingResubmission] = useState(false);

  // Removed hour awards UI from this page; it will be handled in ComponentAssignmentViewer

  const command = searchParams.get('command');
  const id = searchParams.get('id');

  /** Full grader (assignment creator / instructor): can change grades */
  const isFullGradeCommand = command === 'grade';
  /** Teacher of record (read-only): same as projects-directory gradeButRestricted */
  const isTorReadOnlyCommand = command === 'gradeButRestricted';
  const isGradeOrTorView = isFullGradeCommand || isTorReadOnlyCommand;
  const isStudentViewMode = command === 'studentView';

  useEffect(() => {
    if ((!isGradeOrTorView && !isStudentViewMode) || !id) {
      setError('Invalid access — use command=grade, gradeButRestricted, or studentView with id');
      setLoading(false);
      return;
    }

    loadComponentSubmissionData();
  }, [command, id]);

  const loadComponentSubmissionData = async () => {
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }

      // Load component submission
      const submissionResponse = await axios.get(`/api/component-submissions/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const submission = submissionResponse.data.componentSubmission;
      setComponentSubmission(submission);
      setAllowResubmission(submission.allow_resubmission === true);

      // Load component
      const componentResponse = await axios.get(`/api/components/${submission.component}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const componentData = componentResponse.data.component;
      setComponent(componentData);

      // Check if component type is Assignment
      if (componentData.type !== 'Assignment') {
        setError('Only Assignment components can be graded at this time. Other component types are not yet supported.');
        setLoading(false);
        return;
      }

      // Load assignment if assignmentsubmission exists
      if (submission.assignmentsubmission) {
        try {
          const assignmentSubmissionResponse = await axios.get(`/api/assignmentsubmissions/${submission.assignmentsubmission}`, {
            headers: { Authorization: `Bearer ${token}` }
          });

          const assignmentSubmissionData = assignmentSubmissionResponse.data.submission;
          setAssignmentSubmission(assignmentSubmissionData);

          // Load assignment
          const assignmentResponse = await axios.get(`/api/assignments/${assignmentSubmissionData.assignment}`, {
            headers: { Authorization: `Bearer ${token}` }
          });

          const assignmentData = assignmentResponse.data.assignment;
          setAssignment(assignmentData);
        } catch (error) {
          console.error('Error loading assignment data:', error);
          setError('Failed to load assignment data');
          setLoading(false);
          return;
        }
      }

      // Hour awards parsing removed from this page; handled in ComponentAssignmentViewer

    } catch (error) {
      console.error('Error loading component submission:', error);
      setError(error.response?.data?.error || error.message || 'Failed to load component submission');
    } finally {
      setLoading(false);
    }
  };

  // Handle grading update from ComponentAssignmentViewer
  const handleGradingUpdate = async (gradingData) => {
    if (!componentSubmission || !isFullGradeCommand) return;
    try {
      const token = localStorage.getItem('token');
      const updatePayload = {
        status: 'completed',
        hours_earned: gradingData.hours_earned || null,
        completion_data: { completed: true },
        allow_resubmission: false // Auto-reset allow_resubmission when instructor grades
      };
      const response = await axios.put(`/api/component-submissions/${componentSubmission.id}`, updatePayload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Update local state
      setComponentSubmission(response.data.componentSubmission);
      setAllowResubmission(false);
      console.log('Component submission updated successfully');
    } catch (error) {
      console.error('Error updating component submission:', error);
    }
  };

  // Toggle allow_resubmission flag
  const handleToggleAllowResubmission = async () => {
    if (!componentSubmission || !isFullGradeCommand) return;
    
    setUpdatingResubmission(true);
    try {
      const token = localStorage.getItem('token');
      const newValue = !allowResubmission;
      const updatePayload = {
        allow_resubmission: newValue
      };
      const response = await axios.put(`/api/component-submissions/${componentSubmission.id}`, updatePayload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Update local state
      setComponentSubmission(response.data.componentSubmission);
      setAllowResubmission(newValue);
      console.log('Allow resubmission updated:', newValue);
    } catch (error) {
      console.error('Error updating allow resubmission:', error);
      alert('Failed to update resubmission setting. Please try again.');
    } finally {
      setUpdatingResubmission(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <CriticalErrorPopup
        isOpen={true}
        title="Component Submission Error"
        message="Failed to load the component submission for grading."
        details={error}
        showHomeButton={true}
        homeButtonText="Go to Home"
        onHomeClick={() => navigate('/')}
      />
    );
  }

  if (!componentSubmission || !component) {
    return (
      <CriticalErrorPopup
        isOpen={true}
        title="Component Submission Not Found"
        message="The requested component submission could not be found."
        details="Please check the URL and try again."
        showHomeButton={true}
        homeButtonText="Go to Home"
        onHomeClick={() => navigate('/')}
      />
    );
  }

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '2rem 1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <p style={{ color: '#666', fontSize: '16px', margin: 0 }}>
          LMS Component: <strong>{component.title}</strong> | Type: <strong>{component.type}</strong>
          {componentSubmission?.first_name && componentSubmission?.last_name && (
            <> | Student: <strong>{componentSubmission.first_name} {componentSubmission.last_name}</strong></>
          )}
        </p>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {/* Allow Resubmission Toggle - Only show in grade mode for Points assignments that are already graded */}
          {isFullGradeCommand && assignment && assignment.gradingresulttype === 'Points' && componentSubmission?.status === 'completed' && (
            <button
              style={{
                padding: '8px 16px',
                background: allowResubmission ? '#4caf50' : '#f5f5f5',
                color: allowResubmission ? 'white' : '#666',
                border: `2px solid ${allowResubmission ? '#4caf50' : '#ddd'}`,
                borderRadius: 8,
                cursor: updatingResubmission ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.2s ease',
                opacity: updatingResubmission ? 0.6 : 1
              }}
              onClick={handleToggleAllowResubmission}
              disabled={updatingResubmission}
              title={allowResubmission ? 'Click to disable resubmission' : 'Click to allow student to resubmit'}
            >
              {updatingResubmission ? (
                'Updating...'
              ) : (
                <>
                  {allowResubmission ? '✓' : '○'} 
                  Allow Resubmission
                </>
              )}
            </button>
          )}
          {/* Comments Button */}
          <button
            style={{
              padding: 0,
              background: 'none',
              border: '2px solid #1D4E89',
              borderRadius: 8,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'transform 0.2s ease'
            }}
            onClick={() => setShowCommentsModal(true)}
            onMouseOver={e => e.currentTarget.style.transform = 'scale(1.05)'}
            onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
            title="View Comments"
          >
            <svg width="40" height="40" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="48" height="48" rx="12" fill="#F5F7FA"/>
              <path d="M12 16C12 13.7909 13.7909 12 16 12H32C34.2091 12 36 13.7909 36 16V26C36 28.2091 34.2091 30 32 30H22L16 36V30H16C13.7909 30 12 28.2091 12 26V16Z" fill="white" stroke="#4A5568" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="18" cy="21" r="1.5" fill="#4A5568"/>
              <circle cx="24" cy="21" r="1.5" fill="#4A5568"/>
              <circle cx="30" cy="21" r="1.5" fill="#4A5568"/>
            </svg>
          </button>
        </div>
      </div>

      {isTorReadOnlyCommand && (
        <div
          style={{
            marginBottom: '1rem',
            padding: '12px 16px',
            background: '#f3e8ff',
            border: '1px solid #c4b5fd',
            borderRadius: 8,
            color: '#5b21b6',
            fontSize: 14
          }}
        >
          <strong>Teacher of record — read-only.</strong> You can review this submission; only the assignment creator
          can change grades or hours from the full grader.
        </div>
      )}

      {/* Assignment Viewer */}
      {assignment && assignmentSubmission && (
        <div style={{ 
          marginBottom: '2rem', 
          border: '2px solid #1D4E89', 
          borderRadius: '12px', 
          background: '#f9fbff', 
          padding: '24px' 
        }}>
          <h2 style={{ color: '#1D4E89', marginBottom: '1rem' }}></h2>
          <ComponentAssignmentViewer
            assignment={assignment}
            mode={isGradeOrTorView ? 'grade' : 'view'}
            interactive={false}
            submission={assignmentSubmission}
            userData={userData}
            showAnswers={isGradeOrTorView}
            allowGrading={isFullGradeCommand}
            stickyBottomGradingControls={isFullGradeCommand}
            componentHourAwards={component?.hourawards}
            component={component}
            onGradingUpdate={handleGradingUpdate}
            componentSubmissionHoursEarned={componentSubmission?.hours_earned ? (typeof componentSubmission.hours_earned === 'string' ? JSON.parse(componentSubmission.hours_earned) : componentSubmission.hours_earned) : null}
          />
        </div>
      )}

      {/* Hour awards UI removed from this page */}

      {/* Comments Modal */}
      <ProjectComments
        referenceId={componentSubmission.id}
        table="componentsubmissions"
        userData={userData}
        isOpen={showCommentsModal}
        onClose={() => setShowCommentsModal(false)}
        relevantRole={userData?.accesscards?.find(ac => ac.accesscard_id === userData.default_accesscard_id)?.role === 'Student' ? 'Student' : 'Instructor'}
        onEvaluationComment={() => {
          setShowCommentsModal(false);
          // Optionally refresh component submission data if needed
        }}
      />
    </div>
  );
};

export default ComponentSubmissions;
